"use client";

import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  selectActiveHoveredTaskLayout,
  selectDependencyLineLayouts,
  selectHighlightedTaskIds,
  selectTooltipData,
  selectTooltipPlacementData,
  type GanttDependencyLineLayout,
  type TimelineViewportState,
} from "./gantt-render-selectors";
import type { GanttTooltipProps } from "./GanttTooltip";
import type { GanttLayout, ParsedGantt } from "./gantt-types";

export interface UseGanttHoverControllerParams {
  parsed: ParsedGantt | null;
  layout: GanttLayout | null;
  timelineViewportState: TimelineViewportState;
}

export interface UseGanttHoverControllerResult {
  hoveredTaskId: string | null;
  setHoveredTaskId: Dispatch<SetStateAction<string | null>>;
  highlightedTaskIds: Set<string>;
  dependencyLineLayouts: GanttDependencyLineLayout[];
  tooltipPlacementData: GanttTooltipProps["tooltip"];
}

export function useGanttHoverController({
  parsed,
  layout,
  timelineViewportState,
}: UseGanttHoverControllerParams): UseGanttHoverControllerResult {
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (!parsed) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      setHoveredTaskId(null);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [parsed]);

  const activeHoveredTaskLayout = useMemo(() => {
    return selectActiveHoveredTaskLayout({
      layout,
      hoveredTaskId,
    });
  }, [hoveredTaskId, layout]);

  const highlightedTaskIds = useMemo(() => {
    return selectHighlightedTaskIds({
      activeHoveredTaskLayout,
    });
  }, [activeHoveredTaskLayout]);

  const dependencyLineLayouts = useMemo(() => {
    return selectDependencyLineLayouts({
      layout,
      activeHoveredTaskLayout,
    });
  }, [activeHoveredTaskLayout, layout]);

  const tooltipData = useMemo(() => {
    return selectTooltipData({
      activeHoveredTaskLayout,
    });
  }, [activeHoveredTaskLayout]);

  const tooltipPlacementData = useMemo(() => {
    return selectTooltipPlacementData({
      tooltipData,
      timelineViewportState,
    });
  }, [timelineViewportState, tooltipData]);

  return {
    hoveredTaskId,
    setHoveredTaskId,
    highlightedTaskIds,
    dependencyLineLayouts,
    tooltipPlacementData,
  };
}
